<template>
	<!-- header -->
	<Teleport to="#header-search-container">
		<div ref="searchAppEl" class="tw" data-id="search">
			<div v-if="!isScreen1408px" role="search">
				<button
					:id="OPEN_SEARCH_BUTTON_ID"
					class="flex h-11 scroll-mt-1.5 items-center justify-center px-3"
					aria-haspopup="dialog"
					@click="openRightCanvas()"
				>
					<span class="sr-only">{{ t("siteWide.searchApp.search.openSearch") }}</span>
					<!-- search icon is taken from master.tag -->
					<SvgIcon class="size-5" icon-name="search" />
				</button>
			</div>
			<SearchForm v-else />
		</div>
	</Teleport>

	<!-- mobile drawer -->
	<Teleport v-if="!isScreen1408px" to="body">
		<div class="tw">
			<!-- eslint-disable-next-line vuejs-accessibility/click-events-have-key-events eslint-disable-next-line vuejs-accessibility/no-static-element-interactions -->
			<div
				class="srh__drawer-overlay fixed left-0 top-0 z-[999] h-full w-screen justify-start desktop:justify-end"
				:class="[isSearchVisible ? 'flex' : 'hidden', { 'bg-black/70': isDrawerOpen }]"
				:style="{ '--transition-duration': `${animationDurationMs}ms` }"
				@click.self.stop="onCloseSearch"
			>
				<!-- eslint-disable-next-line vuejs-accessibility/click-events-have-key-events eslint-disable-next-line vuejs-accessibility/no-static-element-interactions -->
				<div
					ref="searchDialogEl"
					role="dialog"
					class="srh__drawer flex max-h-full w-[35.7142857rem] max-w-full transform flex-col overflow-hidden overscroll-contain bg-white text-sm"
					:class="{ '-translate-x-full desktop:translate-x-full': !isDrawerOpen }"
					:style="{ height: searchBoxHeight }"
					aria-modal="true"
					aria-labelledby="searchHeadline"
					@keydown.escape="handleDialogEscapeKeyDown"
				>
					<span id="searchHeadline" class="sr-only">{{ t("siteWide.searchApp.search.menuHeadline") }}</span>
					<div class="relative">
						<DepartmentToggle />
						<CloseButton class="right-4 top-2" @click="onCloseSearch" />
					</div>
					<SearchForm in-drawer />
				</div>
			</div>
		</div>
	</Teleport>
</template>

<script setup lang="ts">
import { ref, useTemplateRef, watch, nextTick, onMounted, onBeforeUnmount, onUnmounted } from "vue";
import { useBreakpoints, onClickOutside } from "@vueuse/core";
import { useI18n } from "vue-i18n";

import SvgIcon from "@SharedVueComponents/SvgIcon/SvgIcon.vue";
import SearchForm from "./components/SearchForm.vue";
import DepartmentToggle from "./components/DepartmentToggle.vue";
import CloseButton from "@SharedVueComponents/CloseButton/CloseButton.vue";
import { inertLandmarksAndSkipLinks, removeInertFromLandmarksAndSkipLinks } from "@ocm/services/services.inert";

import useSearch from "./composables/use-search";
import useScrollBehaviourBody from "./composables/use-scroll-behaviour-body";

import OcmMessenger from "ocm_mercurius_messenger";
import { messengerPublicationTypes } from "@ocm/services/services.constants";
import type { MessengerData } from "@ocm/types/OcmMessenger.type";
import { animationDurationMs } from "@ocm/services/services.modal";
import { skipLinks } from "@ocm/services/services.skipLinks";
import { tr_searchClick } from "./services/dataLayer";

const { t } = useI18n();

const OPEN_SEARCH_BUTTON_ID = skipLinks.search.rightDrawerButton.slice(1); // #open-search-right-drawer

const isScreen1408px = useBreakpoints({ px1408: "88em" }).greaterOrEqual("px1408");
const { userInput, isSearchVisible, openSearch, closeSearch, triggerSearch } = useSearch();
const searchAppEl = useTemplateRef("searchAppEl");
const searchDialogEl = useTemplateRef("searchDialogEl");

onClickOutside(searchAppEl, () => isScreen1408px.value && closeSearch());

const searchBoxHeight = ref("");
const _setSearchBoxHeight = () => {
	// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
	searchBoxHeight.value = `${window.visualViewport!.height}px`;
};

const _resetSearchBoxHeight = () => {
	window.visualViewport?.removeEventListener("resize", _setSearchBoxHeight);
	searchBoxHeight.value = "";
};

const timeout = ref<number>();
onBeforeUnmount(() => {
	clearTimeout(timeout.value);
});
const isDrawerOpen = ref(false);

const openRightCanvas = () => {
	tr_searchClick("Icon");
	onOpenSearch();
};

const onOpenSearch = () => {
	// called by (this.)openRichtCanvas() and from outside using messenger: OPEN_SEARCH (left canvas)
	inertLandmarksAndSkipLinks(searchDialogEl.value);
	openSearch();
	setAriaExpanded(true);
};

const searchSubmitted = ref(false);
const _onSearchSubmitted = () => {
	searchSubmitted.value = true;
};

const onCloseSearch = () => {
	removeInertFromLandmarksAndSkipLinks();

	if (isScreen1408px.value) closeSearch();
	else {
		timeout.value = setTimeout(closeSearch, animationDurationMs);
		// if user submitted a search request, focus will be handled in ResultAmount.vue
		if (isDrawerOpen.value && !searchSubmitted.value) focusOpenSearchButton();
		isDrawerOpen.value = false;
		setAriaExpanded(false);
	}
	searchSubmitted.value = false;
};

function setAriaExpanded(value: boolean) {
	document
		.querySelector<HTMLButtonElement>(skipLinks.search.rightDrawerButton)
		?.setAttribute("aria-expanded", `${value}`);
	document
		.querySelector<HTMLButtonElement>(skipLinks.search.leftDrawerButton)
		?.setAttribute("aria-expanded", `${value}`);
}

const { lockBody, unlockBody } = useScrollBehaviourBody();
watch(isSearchVisible, async (isSearchVisible) => {
	if (isSearchVisible) {
		await nextTick();
		if (!isScreen1408px.value) {
			isDrawerOpen.value = true;
			lockBody();
		}
		window.visualViewport?.addEventListener("resize", _setSearchBoxHeight);
	} else {
		isDrawerOpen.value = false;
		unlockBody();
		_resetSearchBoxHeight();
	}
});

watch(isScreen1408px, (isScreen1408px) => {
	if (!isSearchVisible.value) return;
	isDrawerOpen.value = !isScreen1408px;
	isScreen1408px ? unlockBody() : lockBody();
});

const onTriggerSearch = ({ data }: MessengerData) => {
	if (typeof data !== "string") return;
	userInput.value = data;
	triggerSearch();
};

const handleDialogEscapeKeyDown = () => {
	if (isSearchVisible.value) {
		onCloseSearch();
	}
};

const isScreenSmallerThan1024px = useBreakpoints({ px1024: "64em" }).smaller("px1024");
const _skipLinkEl = document.querySelector<HTMLAnchorElement>("#js-skip-to-search");
const _updateSkipLink = () => {
	if (!_skipLinkEl) return;

	switch (true) {
		case isScreenSmallerThan1024px.value:
			_skipLinkEl.setAttribute("href", skipLinks.search.leftDrawerButton);
			break;
		case isScreen1408px.value:
			_skipLinkEl.setAttribute("href", skipLinks.search.input);
			break;
		default:
			_skipLinkEl.setAttribute("href", skipLinks.search.rightDrawerButton);
	}
};

onMounted(() => {
	OcmMessenger.subscribe(messengerPublicationTypes.OPEN_SEARCH, onOpenSearch);
	OcmMessenger.subscribe(messengerPublicationTypes.SEARCH_SUBMITTED, _onSearchSubmitted);
	OcmMessenger.subscribe(messengerPublicationTypes.CLOSE_SEARCH, onCloseSearch);

	if (document.querySelectorAll("[data-id=search]").length === 1) {
		OcmMessenger.subscribe(messengerPublicationTypes.TRIGGER_SEARCH, onTriggerSearch);
	}

	window.addEventListener("resize", _updateSkipLink);
	_updateSkipLink();
});

onUnmounted(() => {
	_resetSearchBoxHeight();
	window.removeEventListener("resize", _updateSkipLink);
});

function focusOpenSearchButton() {
	switch (true) {
		case isScreenSmallerThan1024px.value:
			document.querySelector<HTMLButtonElement>(skipLinks.search.leftDrawerButton)?.focus();
			break;
		case !isScreenSmallerThan1024px.value && !isScreen1408px.value:
			document.querySelector<HTMLButtonElement>(skipLinks.search.rightDrawerButton)?.focus();
			break;
	}
}
</script>

<style>
.srh__drawer-overlay {
	transition: background-color var(--transition-duration, 320ms);
}

.srh__drawer {
	transition: transform var(--transition-duration, 320ms);
}
</style>
